/**
 * @architecture-header 1
 * @layer module-infrastructure
 * @domain settings-student
 * @responsibility Own the SET-F10 organization-session password-change command. Profile
 *   (display name/avatar) and language reuse SET-F02/SET-F03's own public hooks verbatim --
 *   confirmed unmodified for a student actor -- so this file owns only the operation genuinely
 *   new to this card.
 * @state-owner TanStack Query mutation state
 * @allowed-imports app environment; same-module API and contracts; TanStack Query
 * @forbidden component UI; fabricated password state; direct Supabase calls
 * @public-api settings-student hooks
 * @side-effects authenticated password-change request
 * @tests frontend/packages/workspace-web/src/modules/settings-student/components/StudentSettingsSurface.test.tsx
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase, EXCEPT change_organization_password's own request/response fields, which
 *   are genuinely snake_case on the wire (see settings-student.ts's own header note)
 */

import { useMutation } from '@tanstack/react-query';
import { VispoApiError } from '@vispo/api-client';
import type { ChangeOrganizationPasswordInput, OrganizationSessionContext } from '@vispo/contracts';
import { useOptionalWorkspaceEnvironment } from '../../../app/config/environment';
import { useWorkspaceSession } from '../../../app/providers/SessionProvider';
import { changeOrganizationPassword, readOrganizationCsrfToken } from './settings-student';

type DemoPasswordChangeFailure = 'wrong-current-password' | 'weak-new-password' | 'unavailable';

function demoFailureError(failure: DemoPasswordChangeFailure): VispoApiError {
  if (failure === 'wrong-current-password') {
    return new VispoApiError(
      {
        code: 'ORG_AUTH_INVALID',
        message: 'The credentials are invalid or cannot be used.',
        details: undefined,
        requestId: 'demo',
      },
      401,
    );
  }
  if (failure === 'weak-new-password') {
    return new VispoApiError(
      {
        code: 'ORG_AUTH_PASSWORD_REJECTED',
        message: 'That password cannot be used.',
        details: undefined,
        requestId: 'demo',
      },
      422,
    );
  }
  return new VispoApiError(
    {
      code: 'AUTH_PROVIDER_UNAVAILABLE',
      message: 'Authentication is temporarily unavailable.',
      details: undefined,
      requestId: 'demo',
    },
    503,
  );
}

export function useResetOrganizationPassword() {
  const environment = useOptionalWorkspaceEnvironment();
  const { acceptOrganizationSession, session } = useWorkspaceSession();

  return useMutation({
    mutationFn: async (
      input: ChangeOrganizationPasswordInput,
    ): Promise<OrganizationSessionContext> => {
      if (environment?.demoMode) {
        const e2e = globalThis as typeof globalThis & {
          __VISPO_SET_F10_PASSWORD_CHANGE_FAILURE__?: DemoPasswordChangeFailure;
        };
        const failure = e2e.__VISPO_SET_F10_PASSWORD_CHANGE_FAILURE__;
        if (failure) throw demoFailureError(failure);
        return {
          status: 'authenticated',
          // The CURRENT session's own userId, never a distinct hardcoded one: feeding a
          // different id through acceptOrganizationSession below would change every
          // session.userId-keyed query key (useProfile/usePreferences/useActiveContext) mid-flow,
          // switching the whole demo identity instead of just rotating its CSRF proof.
          user_id: session?.userId ?? 'demo-org-login-student',
          csrf_token: `demo-csrf-${crypto.randomUUID()}`,
          expires_at: new Date(Date.now() + 12 * 60 * 60 * 1000).toISOString(),
        };
      }
      if (!environment) throw new Error('WorkspaceEnvironment is missing');
      const csrfToken = readOrganizationCsrfToken();
      if (!csrfToken) throw new Error('Organization session is missing its CSRF proof');
      return changeOrganizationPassword(environment.apiClient, input, csrfToken);
    },
    // The rotated csrf_token this response carries is the ONLY proof of it that exists after this
    // point -- the old X-CSRF-Token header is now stale server-side. Feeding it through
    // acceptOrganizationSession keeps SessionProvider's own organizationCsrfRef current, so the
    // next signOut() (or any other organization-session call) sends the token the server actually
    // expects instead of the pre-rotation one.
    onSuccess: (response) => acceptOrganizationSession(response),
  });
}
